import * as yup from "yup";
import { createAddressFields } from "../constants";
import { type FormSectionConfig, type TaxFormConfig, type SelectOption } from "../types";

export const W8ECI_ENTITY_TYPE_OPTIONS: SelectOption[] = [
  { value: "partnership", label: "Partnership" },
  {
    value: "foreign_government_controlled_entity",
    label: "Foreign Government - Controlled Entity",
  },
  { value: "foreign_government_integral_part", label: "Foreign Government - Integral Part" },
  { value: "private_foundation", label: "Private foundation" },
  { value: "simple_trust", label: "Simple trust" },
  { value: "grantor_trust", label: "Grantor trust" },
  { value: "international_organization", label: "International organization" },
  { value: "individual", label: "Individual" },
  { value: "complex_trust", label: "Complex trust" },
  { value: "central_bank_of_issue", label: "Central bank of issue" },
  { value: "corporation", label: "Corporation" },
  { value: "estate", label: "Estate" },
  { value: "tax_exempt_organization", label: "Tax-exempt organization" },
];

export const W8ECI_US_TIN_TYPE_OPTIONS: SelectOption[] = [
  { value: "ssn", label: "SSN" },
  { value: "itin", label: "ITIN" },
  { value: "ein", label: "EIN" },
];

const sections: FormSectionConfig[] = [
  {
    id: "identification",
    title: "Identification of Beneficial Owner",
    fields: [
      {
        name: "entityName",
        type: "text",
        label: "Entity name",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "countryOfIncorporation",
        type: "country",
        label: "Country of incorporation",
        placeholder: "Select a country",
        required: true,
        gridColumn: "half",
      },
      {
        name: "entityType",
        type: "select",
        label: "Entity type",
        placeholder: "Select the type",
        required: true,
        gridColumn: "full",
        options: W8ECI_ENTITY_TYPE_OPTIONS,
      },
      {
        name: "_foreignAddressLabel",
        type: "label",
        label: "Foreign address",
        required: true,
        linkedField: "foreignAddress",
        gridColumn: "full",
      },
      ...createAddressFields("foreignAddress", {
        helpText: "Do not use a P.O. box or in-care-of address",
      }),
      {
        name: "_usBusinessAddressLabel",
        type: "label",
        label: "US business address",
        linkedField: "usBusinessAddress",
        gridColumn: "full",
      },
      ...createAddressFields("usBusinessAddress", {
        helpText: "Do not use a P.O. box or in-care-of address",
      }),
      {
        name: "usTinType",
        type: "select",
        label: "US TIN type",
        placeholder: "Select a type",
        required: true,
        gridColumn: "half",
        options: W8ECI_US_TIN_TYPE_OPTIONS,
        plainLabel: true,
      },
      {
        name: "usTin",
        type: "text",
        label: "US TIN",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "incomeDescription",
        type: "textarea",
        label: "Income description",
        placeholder: "Specify each item of income",
        required: true,
        gridColumn: "full",
      },
      {
        name: "certifyAsDealerPtp",
        type: "checkbox",
        label: "Check to certify as Dealer/PTP",
        gridColumn: "full",
      },
    ],
  },
];

const validationSchema = yup.object().shape({
  entityName: yup.string().required("Entity name is required"),
  countryOfIncorporation: yup.string().required("Country of incorporation is required"),
  entityType: yup.string().required("Entity type is required"),
  foreignAddress: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  usTinType: yup.string().required("US TIN type is required"),
  usTin: yup.string().required("US TIN is required"),
  incomeDescription: yup.string().required("Income description is required"),
});

const initialValues = {
  entityName: "",
  countryOfIncorporation: "",
  entityType: "",
  foreignAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  usBusinessAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  usTinType: "",
  usTin: "",
  incomeDescription: "",
  certifyAsDealerPtp: false,
};

export const w8eciConfig: TaxFormConfig = {
  formId: "W8ECI",
  title: "New FATCA details",
  subtitle: "W-8ECI",
  sections,
  validationSchema,
  initialValues,
};
